import { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
	getHeartBeatStatus,
	pollHeartBeatStatusStop,
	setHeartBeatStatusSyncCount,
} from '@Actions';
import { HeartBeatStatus } from '@Types';
import { getValue } from '@Selectors';

type HeartBeatStatusCheckerReturn = {
	heartBeatStatus: HeartBeatStatus;
	handleGetHeartBeatStatus: () => void;
	handleGetHeartBeatStatusStop: () => void;
	heartBeatSyncAtTs: number;
	heartBeatFirstPoll: boolean;
};

const HEARTBEAT_STOP_AFTER_SEC = 3600; // * 1HR (in seconds)
const HEARTBEAT_POLL_DELAY_SEC = 15; // * 4 polls per minute

const useHeartBeatStatusChecker = (): HeartBeatStatusCheckerReturn => {
	const dispatch = useDispatch();

	const {
		heartBeatDetails: {
			heartBeatStatus,
			heartBeatSyncAtTs,
			heartbeatSyncCount,
		},
	} = useSelector(getValue);

	const handleGetHeartBeatStatus = () => {
		dispatch(
			getHeartBeatStatus({
				pollDelaySec: HEARTBEAT_POLL_DELAY_SEC,
				stopAfterSec: HEARTBEAT_STOP_AFTER_SEC, // ? optional
			}),
		);
	};

	const handleGetHeartBeatStatusStop = () => {
		dispatch(pollHeartBeatStatusStop());
	};

	useEffect(() => {
		dispatch(setHeartBeatStatusSyncCount(0));
	}, []);

	useEffect(() => {
		handleGetHeartBeatStatus();
		return () => {
			handleGetHeartBeatStatusStop(); // * Teardown (component unmount)
		};
	}, []);

	const heartBeatFirstPoll = useMemo(
		() => heartbeatSyncCount <= 1,
		[heartbeatSyncCount],
	);

	return {
		heartBeatStatus,
		handleGetHeartBeatStatus,
		handleGetHeartBeatStatusStop,
		heartBeatSyncAtTs,
		heartBeatFirstPoll,
	};
};

export default useHeartBeatStatusChecker;
